// Single source of truth for the six feature modules — read by both
// LandingPage.jsx (card grid + popup) and Features.jsx (the /features page),
// so the two can never drift apart again. Copy here has been verified
// against the live app (see the /features build): no SendGrid/Twilio/Resend
// naming, no automatic lease-expiration or maintenance messaging (neither
// exists), and late fees are described as tracked, not auto-applied, since
// the only code that applied them automatically had no call sites anywhere.
// Texting/SMS is not an offered feature — the "alerts" entry is email only.
//
// `id` doubles as the /features anchor (e.g. /features#tenant-management) —
// keep these in sync with Features.jsx's section ids if either changes.
const SAGE = "#7FB98E";

export const FEATURES_CONTENT = [
  {
    id: "tenant-management",
    icon: <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={SAGE} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
    title: "Tenant Management",
    brief: "Complete profiles for every tenant and unit.",
    intro: "Track every tenant from move-in to move-out. Store contact info, lease dates, unit assignments, and payment history in one clean dashboard. Get instant visibility into who's current, expiring, or overdue.",
    bullets: [
      "Full tenant profiles with contact info and lease dates",
      "Status tracking: active, expiring, or month-to-month",
      "Unit assignment across multiple properties",
      "Communication log with full history per tenant",
      "Tenant photo documentation",
    ],
  },
  {
    id: "rent-payments",
    icon: <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={SAGE} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>,
    title: "Rent & Payments",
    brief: "Log payments, track overdue balances instantly.",
    intro: "Record payments in seconds. Overdue balances are flagged automatically. Receipts sent by email or text. Full payment history by tenant or property with branded invoice generation.",
    bullets: [
      "One-click payment recording with date and method",
      "Automatic overdue flagging",
      "Late fee tracking on every payment",
      "Branded invoice generation with your logo",
      "Monthly and annual payment history reports",
    ],
  },
  {
    id: "maintenance",
    icon: <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={SAGE} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg>,
    title: "Maintenance Tracking",
    brief: "Submit, assign, and resolve requests with full history.",
    intro: "Every maintenance request from open to resolved, tracked in one place. Assign contractors, log costs, and keep a full history on every ticket. Priority levels and category tags keep nothing falling through the cracks.",
    bullets: [
      "Open, in-progress, and resolved ticket views",
      "Contractor assignment and cost tracking",
      "Priority levels: high, medium, and low",
      "Category tags: plumbing, electrical, HVAC, and more",
      "Full note history and resolution log on every ticket",
    ],
  },
  {
    id: "financial-reports",
    icon: <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={SAGE} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
    title: "Financial Reports",
    brief: "Income, expenses, and P&L ready for tax season.",
    intro: "See your full portfolio performance at a glance. Monthly and annual breakdowns by property. Expense tracking by category. Net profit calculations. Export-ready reports for tax preparation or investor review.",
    bullets: [
      "Portfolio-wide P&L with monthly breakdown",
      "Per-property income and expense reporting",
      "Category-based expense tracking",
      "Net profit calculations with tax estimates",
      "Tax summary and CSV export for accounting and tax preparation",
      "Every report you pull is saved, so you can open or download it again",
    ],
  },
  {
    id: "documents",
    icon: <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={SAGE} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
    title: "Document Center",
    brief: "Generate leases, notices, and letters in minutes.",
    intro: "Professional document generation in minutes. Pre-built templates for rent increase notices, lease renewals, move-in letters, inspection notices, and more. Fill in tenant-specific details and save to the document library.",
    bullets: [
      "Six pre-built letter and notice templates",
      "Custom template builder for your own formats",
      "Auto-fill with tenant name, rent, and lease data",
      "Saved document library organized by tenant",
      "Download or print",
    ],
  },
  {
    id: "alerts",
    icon: <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={SAGE} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>,
    title: "Email Receipts",
    brief: "Send payment receipts and invoices by email.",
    intro: "Send payment receipts and invoices to tenants by email, straight from Leasly, with your branding on every one.",
    bullets: [
      "Email delivery of receipts and invoices",
      "Logged communication history per tenant",
    ],
  },
];
